import { useEffect, useState, useRef, StrictMode } from "react";
import { __ } from "@wordpress/i18n";
import { Button } from "@wordpress/components";

/**
 * Internal dependencies
 */
import { getProcesses } from "./getProcesses";
/**
 * ProcessesList component.
 *
 * @export
 * @return {Element}
 */
export default function ProcessesList() {
    /**
     * Processes API states.
     * List of processes, current page, total pages, loading state and items per page.
     * @type {Array}
     * @type {Number}
     * @type {Number}
     * @type {Boolean}
     * @type {Number}
     */
    const [processes, setProcesses] = useState([
        {
            ClaimId: 0,
            NumeroReuniao: 0,
            AnoReuniao: 0,
            DataDecisao: "",
            DecisaoComissao: "",
            HouveNovaDecisaoFinal: "",
            NumeroProcesso: "",
            TipoCrime: "",
            URLDecisaoFinal: "",
        },
    ]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [years, setYears] = useState([0]);
    const [filters, setFilters] = useState({
        year: 0,
        processNum: "",
        crimeType: "",
        finalDecision: "",
    });
    const [clearFilters, setClearFilters] = useState(false);
    const formRef = useRef(null);

    function scrollToView() {
        const elementPosition = formRef.current.offsetTop;
        window.scrollTo({
            top: elementPosition - 180,
            behavior: "smooth",
        });
    }
    /**
     * Fetch processes from the API.
     * @returns {Promise<void>}
     * @async
     */
    const fetchProcesses = async () => {
        setLoading(true);
        console.log()
        const res = await getProcesses(
            filters.year,
            filters.processNum,
            filters.crimeType,
            filters.finalDecision,
            currentPage,
            itemsPerPage,
        );
        if (res) {
            setProcesses(res.InfoProcess);
            setTotalItems(res.CountResults);
        }
        setLoading(false);
    };
    /**
     * Change the current page.
     * @param {number} page
     */
    const handlePageChange = (page) => {
        scrollToView();
        setCurrentPage(page);
    };

    /**
     * Change the items per page.
     * @param {Event} e
     * @returns {void}
     */
    const handleItemsPerPageChange = (e) => {
        scrollToView();
        setItemsPerPage(parseInt(e.target.value));
    };

    const handleFilter = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchProcesses();
        scrollToView();
    };

    const handleClear = async () => {
        setFilters({
            year: 0,
            processNum: "",
            crimeType: "",
            finalDecision: "",
        });
        setCurrentPage(1);
        setClearFilters(prev => !prev);
    };

    /**
     * Fetch processes and set years on component mount.
     * @returns {void}
     */
    useEffect(() => {
        fetchProcesses();
        setYears(
            Array.from(
                { length: new Date().getFullYear() - 2024 + 1 },
                (_, index) => new Date().getFullYear() - index,
            ),
        );
    }, []);
    /**
     * Fetch processes when the current page changes. And when the items per page changes.
     */
    useEffect(() => {
        fetchProcesses();
    }, [itemsPerPage, currentPage, clearFilters]);

    return (
        <StrictMode>
            <form
                className="processes-list__filters"
                ref={formRef}
            >
                <div
                    className="form-control"
                >
                    <label htmlFor="processNum">
                        {__("Número do processo", "cpvc")}
                    </label>
                    <input
                        type="text"
                        id="processNum"
                        placeholder="Insira o número do processo"
                        value={filters.processNum}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                processNum: e.target.value,
                            })
                        }
                    />
                </div>
                <div className="form-control">
                    <label htmlFor="claimId">{__("Ano", "cpvc")}</label>
                    <select
                        id="claimId"
                        value={filters.year}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                year: parseInt(e.target.value),
                            })
                        }
                    >
                        <option value="">{__("Selecione", "cpvc")}</option>
                        {years.map((year) => (
                            <option
                                key={year}
                                value={year}
                            >
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-control">
                    <label htmlFor="crimeType">
                        {__("Tipo de Crime", "cpvc")}
                    </label>
                    <select
                        id="crimeType"
                        value={filters.crimeType}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                crimeType: e.target.value,
                            })
                        }
                    >
                        <option value="">{__("Selecione", "cpvc")}</option>
                        <option value="Crime Violento">
                            {__("Crime Violento", "cpvc")}
                        </option>
                        <option value="Violência Doméstica">
                            {__("Violência Doméstica", "cpvc")}
                        </option>
                    </select>
                </div>
                <div className="form-control">
                    <label htmlFor="finalDecision">
                        {__("Decisão Final", "cpvc")}
                    </label>
                    <select
                        id="finalDecision"
                        value={filters.finalDecision}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                finalDecision: e.target.value,
                            })
                        }
                    >
                        <option value="">{__("Selecione", "cpvc")}</option>
                        <option value="Deferido">
                            {__("Deferido", "cpvc")}
                        </option>
                        <option value="Indeferido">
                            {__("Indeferido", "cpvc")}
                        </option>
                    </select>
                </div>
                <div className="form-actions">
                    <Button
                        variant="secondary"
                        disabled={loading || (!filters.year && !filters.processNum && !filters.crimeType && !filters.finalDecision)}
                        onClick={handleClear}
                    >
                        {__("Limpar", "cpvc")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleFilter}
                        disabled={loading || (!filters.year && !filters.processNum && !filters.crimeType && !filters.finalDecision)}
                        type="submit"
                    >
                        {__("Filtrar", "cpvc")}
                    </Button>
                </div>
            </form>
            {!loading ? (
                <table className="processes-list__table">
                    <caption></caption>
                    <thead>
                        <tr>
                            <th>{__("Nº da Reunião da Decisão Final", "cpvc")}</th>
                            <th>{__("Data da decisão", "cpvc")}</th>
                            <th>{__("Nº do Processo", "cpvc")}</th>
                            <th>{__("Tipo de crime", "cpvc")}</th>
                            <th>{__("Houve nova decisão final?", "cpvc")}</th>
                            <th>{__("Decisão final", "cpvc")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {totalItems > 0 ? (
                            processes?.map((process) => (
                                <tr key={process.NumeroProcesso}>
                                    <td
                                        data-header={__(
                                            "Nº da reunião",
                                            "cpvc",
                                        )}
                                    >
                                        {process.NumeroReuniao}
                                    </td>
                                    <td
                                        data-header={__(
                                            "Data da decisão",
                                            "cpvc",
                                        )}
                                    >
                                        {process.DataDecisao}
                                    </td>
                                    <td
                                        data-header={__(
                                            "Nº do Processo",
                                            "cpvc",
                                        )}
                                    >
                                        {process.NumeroProcesso}
                                    </td>
                                    <td
                                        data-header={__(
                                            "Tipo de crime",
                                            "cpvc",
                                        )}
                                    >
                                        {process.TipoCrime}
                                    </td>
                                    <td
                                        data-header={__(
                                            "Houve nova decisão final? ",
                                            "cpvc",
                                        )}
                                    >
                                        {process.HouveNovaDecisaoFinal}
                                    </td>
                                    <td
                                        data-header={__(
                                            "Decisão final",
                                            "cpvc",
                                        )}
                                    >
                                        <a
                                            href={process.URLDecisaoFinal}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {__(
                                                process.DecisaoComissao,
                                                "cpvc",
                                            )}
                                        </a>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6">
                                    {__("Nenhum resultado encontrado", "cpvc")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="6">
                                <label htmlFor="itemsPerPage">
                                    {__("Linhas por página:")}
                                </label>
                                <select
                                    id="itemsPerPage"
                                    onChange={(e) =>
                                        handleItemsPerPageChange(e)
                                    }
                                    value={itemsPerPage}
                                    disabled={loading || totalItems < 10}
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="30">30</option>
                                    <option value="40">40</option>
                                    <option value="50">50</option>
                                </select>
                                <span>{`${
                                    (currentPage - 1) * itemsPerPage + 1
                                } a ${Math.min(
                                    currentPage * itemsPerPage,
                                    totalItems,
                                )} de ${totalItems} items`}</span>
                                <button
                                    onClick={() =>
                                        handlePageChange(currentPage - 1)
                                    }
                                    className={`btn_prev ${
                                        currentPage === 1 ? "disabled" : ""
                                    }`}
                                    aria-label="Previous page"
                                    disabled={
                                        currentPage === 1 || totalItems === 0
                                    }
                                >
                                    &nbsp;
                                </button>
                                <button
                                    onClick={() =>
                                        handlePageChange(currentPage + 1)
                                    }
                                    className={`btn_next ${
                                        currentPage ===
                                            Math.ceil(
                                                totalItems / itemsPerPage,
                                            ) ||
                                        loading ||
                                        totalItems === 0
                                            ? "disabled"
                                            : ""
                                    }`}
                                    aria-label="Next page"
                                    disabled={
                                        currentPage ===
                                            Math.ceil(
                                                totalItems / itemsPerPage,
                                            ) ||
                                        loading ||
                                        totalItems === 0
                                    }
                                >
                                    &nbsp;
                                </button>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            ) : (
                <div className="loading">
                    <span>{__("A carregar...", "cpvc")}</span>
                </div>
            )}
        </StrictMode>
    );
}
